// @ts-strict-ignore
import { BufferGeometry, Float32BufferAttribute, Material, Mesh, Object3D, Vector3 } from 'three';
import { Connection } from '@3d-scene/model3d/connection';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';

export class ArticlesCreator {
  public static stretchConnection(connection: Connection, length: number, inverse: boolean = false): Mesh {
    this.updateConnectionMesh(connection, length, inverse);
    return connection.mesh;
  }

  public static updateConnectionMesh(connection: Connection, length: number, inverse: boolean = false): void {
    const connRectangle: Rectangle3D = connection.localRectangle;
    if (inverse) {
      connRectangle.point2 = MathUtils.getInterpolated(
        connRectangle.point2,
        connRectangle.point1,
        length / connRectangle.point1.distanceTo(connRectangle.point2)
      );
      connRectangle.point3 = MathUtils.getInterpolated(
        connRectangle.point3,
        connRectangle.point4,
        length / connRectangle.point3.distanceTo(connRectangle.point4)
      );
    } else {
      connRectangle.point4 = MathUtils.getInterpolated(
        connRectangle.point4,
        connRectangle.point1,
        length / connRectangle.point1.distanceTo(connRectangle.point4)
      );
      connRectangle.point3 = MathUtils.getInterpolated(
        connRectangle.point3,
        connRectangle.point2,
        length / connRectangle.point2.distanceTo(connRectangle.point3)
      );
    }

    const mesh: Mesh = this.createPlaneForRectangle(connRectangle);
    mesh.material = connection.mesh.material;
    mesh.name = connection.id;
    connection.setMesh(mesh);
  }

  public static createPlaneForRectangle(rectangle: Rectangle3D): Mesh {
    if (!rectangle) {
      return null;
    }
    const vertices = [
      rectangle.point1.x, rectangle.point1.y, rectangle.point1.z,
      rectangle.point2.x, rectangle.point2.y, rectangle.point2.z,
      rectangle.point3.x, rectangle.point3.y, rectangle.point3.z,
      rectangle.point4.x, rectangle.point4.y, rectangle.point4.z
    ];

    // Zwei Dreiecke: (0,1,3) und (1,2,3)
    const indices = [
      0, 1, 3,
      1, 2, 3
    ];

    const uvs = [
      0, 0, // point1
      1, 0, // point2
      1, 1, // point3
      0, 1  // point4
    ];

    const geometry = new BufferGeometry();
    geometry.setIndex(indices);
    geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geometry.computeVertexNormals();

    const mesh = new Mesh(geometry);
    return mesh;
  }

  public static createDynamicArticle(mesh: Mesh, length: number): Mesh {
    const extrusion: Mesh = MeshUtils.cloneMesh(mesh);
    // stretch faces with direction (-1,0,0) by x-axis
    return MeshUtils.extrude(extrusion, length, '-x');
  }

  public static createRangeOnLineConnection(
    mesh: Mesh,
    meta: ConnectionAreaVO,
    connection: Connection,
    length: number
  ): Mesh {
    if (!ConnectionInfoUtils.isRangeOnLineConnection(meta)) {
      return null;
    }
    return this.stretchRangeOnLineConnection(mesh, connection, length);
  }

  public static stretchRangeOnLineConnection(mesh: Mesh, connection: Connection, length: number): Mesh {
    ArticlesCreator.updateConnectionMesh(connection, length);
    (connection.mesh.material as Material).visible = false;
    return connection.mesh;
  }

  public static updateRangeOnLineConnection(
    container: ArticleContainer,
    articleVO: ArticleVO,
    offset: number
  ): boolean {
    if (!container) {
      return false;
    }
    if (container.model instanceof Object3D) {
      return this.stretchRangOnLineConnectionMesh(container, container.model, offset);
    } else {
      return false;
    }
  }

  private static stretchRangOnLineConnectionMesh(
    articleContainer: ArticleContainer,
    container: Object3D,
    offset: number
  ): boolean {
    let meshWasChanged: boolean = false;
    for (const child of Object3DUtils.getChildren(container)) {
      const childMeshWasChanged: boolean = this.stretchRangOnLineConnectionMesh(articleContainer, child, offset);
      meshWasChanged = meshWasChanged || childMeshWasChanged;
      if (IsThreeJsType.mesh(child)) {
        const connection: Connection = articleContainer.findConnectionByName(child.name);
        if (!connection) {
          // if mounting rail
        } else {
          // if connection
          const meta: MetaElementVO = connection.metaElementVO;
          if (meta && meta instanceof ConnectionAreaVO) {
            const connectionMesh: Object3D = this.reCreateRangeOnLineConnection(
              meta as ConnectionAreaVO,
              connection,
              offset
            );
            if (connectionMesh) {
              container.remove(child);
              // TODO: (from flex) check, why child is not destroyed
              container.add(connectionMesh);
              meshWasChanged = true;
            }
          }
        }
      }
    }
    return meshWasChanged;
  }

  private static reCreateRangeOnLineConnection(
    meta: ConnectionAreaVO,
    connection: Connection,
    offset: number
  ): Object3D {
    if (ConnectionInfoUtils.isDrillingHoleRangeOnLineConnection(meta)) {
      this.stretchRangeOnLineConnectionMesh(connection, offset);
      (connection.mesh.material as Material).visible = false;
      return connection.mesh;
    }
    return null;
  }

  private static stretchRangeOnLineConnectionMesh(connection: Connection, offset: number = 2000): void {
    const connRectangle: Rectangle3D = connection.localRectangle;
    connRectangle.point1 = connRectangle.point1.clone().add(new Vector3(offset / 2, 0, 0));
    connRectangle.point4 = connRectangle.point4.clone().sub(new Vector3(offset / 2, 0, 0));
    connRectangle.point3 = connRectangle.point3.clone().sub(new Vector3(offset / 2, 0, 0));
    connRectangle.point2 = connRectangle.point2.clone().add(new Vector3(offset / 2, 0, 0));

    const mesh: Mesh = ArticlesCreator.createPlaneForRectangle(connRectangle);
    mesh.material = connection.mesh.material;
    mesh.name = connection.id;
    connection.setMesh(mesh);
  }
}
